import modal from "./modal";
import {
	EVENT_SET_VALUE,
	EVENT_SEND_VALUE,
	EVENT_GET_VALUE,
	EVENT_CLEAR_VALUE,
	FIELD_KEYS
} from "constants.js";
import {
	find,
	forEach
} from "lodash";
import "./index.less";
import { degreesToDms } from "../../../services/geo-service";
import { idsMatch } from "../../../utils/valueUtilities";

export class Controller {
	constructor($uibModal, eventBus, auth, $scope, $logger) {
		this._uibModal = $uibModal;
		this._scope = $scope;
		this.$logger = $logger;

		this.lookupId = auth.uuid();
		this.location = {};
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
	}

	getFieldValues() {
		const locationPromises = [];

		forEach(this.lookup.valueFields, (field) => {
			const fieldPromise = new Promise((resolve) => {
				const registration = this._eventBus.register(EVENT_SEND_VALUE(this.lookupId), (e, params) => {
					let locationValue = params.value?.[0]?.Number ?? null;

					if(locationValue != null) {
						const {
							degrees,
							minutes,
							isNegative
						} = degreesToDms(locationValue, false);
						locationValue = (degrees + minutes / 60) * (isNegative ? -1 : 1);
					}
					this._eventBus.unregister(registration);
					resolve({ key: params.key, value: locationValue });
				}, this._scope);

				this._eventBus.trigger(EVENT_GET_VALUE(field.fieldId), { id: this.lookupId });
			});

			locationPromises.push(fieldPromise);
		});

		return Promise.all(locationPromises);
	}

	setField(fieldKey, value) {
		const field = find(this.lookup.fields, (f) => f.Key.Code == fieldKey);

		this._eventBus.trigger(EVENT_SET_VALUE(field.Id), { value: { Number: value } });
	}

	findFieldDataItemById(field, selectedId) {
		const data = field?.Data ?? [];
		const directMatch = data.find(item => idsMatch(item.Id, selectedId));

		if(directMatch) {
			return directMatch;
		}

		const nestedEntityMatch = data.reduce((match, item) => {
			if(match) {
				return match;
			}

			const relatedEntity = (item?.Entities ?? []).find(entry => idsMatch(entry.Entity?.Id, selectedId));
			return relatedEntity?.Entity ?? null;
		}, null);

		return nestedEntityMatch;
	}

	getRelatedFieldValues() {
		const relatedFields = this.lookup.relatedFields ?? [];

		if(!relatedFields.length) {
			return Promise.resolve([]);
		}

		const requestId = `${this.lookupId}-related-${Date.now()}`;
		const relatedPromises = relatedFields.reduce((promises, field) => {
			promises.push(new Promise((resolve) => {
				const registration = this._eventBus.register(EVENT_SEND_VALUE(requestId), (e, params) => {
					this._eventBus.unregister(registration);

					const payload = params.value?.[0] ?? {};
					const selectedId = payload.DataId ?? payload.EntityId ?? payload.Id;
					const selectedItem = this.findFieldDataItemById(field, selectedId);

					resolve(selectedItem ? {
						Id: selectedItem.Id,
						Name: selectedItem.Name ?? selectedItem.Code ?? payload._code,
						fieldId: field.Id
					} : null);
				}, this._scope);

				this._eventBus.trigger(EVENT_GET_VALUE(field.Id), { id: requestId });
			}));

			return promises;
		}, []);

		return Promise.all(relatedPromises).then(values => values.reduce((savedValues, value) => {
			if(value) {
				savedValues.push(value);
			}

			return savedValues;
		}, []));
	}

	setRelatedFields(selection) {
		const relatedValues = selection?.relatedValues ?? [];
		const clearedRelatedFieldIds = selection?.clearedRelatedFieldIds ?? [];

		clearedRelatedFieldIds.forEach((fieldId) => {
			this._eventBus.trigger(EVENT_CLEAR_VALUE(fieldId));
		});

		relatedValues.forEach((value) => {
			if(value && value.fieldId && value.Id) {
				this._eventBus.trigger(EVENT_SET_VALUE(value.fieldId), { value });
			}
		});
	}

	activateModal() {
		if(this.lookup.getCurrentValue) {
			this.activateCoupledModal();
		}
		else {
			this.activateUncoupledModal();
		}
	}

	activateCoupledModal() {
		const fieldValue = this.lookup.getCurrentValue()?.[0];
		this.getRelatedFieldValues().then((selectedRelatedValues) => {
			this.showTooltip = false;
			this._uibModal
				.open({
					component: "locationModal",
					size: "md",
					resolve: {
						lookup: () => this.lookup,
						locationFieldName: () => this.lookup.fieldName,
						location: () => (!!fieldValue ? {
							[FIELD_KEYS.latitude]: fieldValue.Latitude,
							[FIELD_KEYS.longitude]: fieldValue.Longitude
						} : undefined),
						selectedRelatedValues: () => selectedRelatedValues,
						defaultPosition: () => (
							!!this.lookup.defaultPosition && this.lookup.defaultPosition.center && this.lookup.defaultPosition.zoom ? {
								center: {
									[FIELD_KEYS.latitude]: this.lookup.defaultPosition.center[0],
									[FIELD_KEYS.longitude]: this.lookup.defaultPosition.center[1]
								},
								zoom: this.lookup.defaultPosition.zoom
							} : undefined
						)
					}
				})
				.result.then(selection => {
					if(selection?.lat != null && selection?.lng != null) {
						this.lookup.action(selection);
					}

					this.setRelatedFields(selection);
				});
		});
	}

	activateUncoupledModal() {
		Promise.all([this.getFieldValues(), this.getRelatedFieldValues()]).then(([values, selectedRelatedValues]) => {
			const valuesObj = {};
			forEach(values, (value) => {
				valuesObj[value.key] = value.value;
			});

			this.showTooltip = false;
			this._uibModal
				.open({
					component: "locationModal",
					size: "md",
					resolve: {
						lookup: () => this.lookup,
						locationFieldName: () => this.lookup.fieldName,
						location: () => valuesObj,
						selectedRelatedValues: () => selectedRelatedValues
					}
				})
				.result.then(selection => {
					if(selection?.lat != null && selection?.lng != null) {
						this.setField(FIELD_KEYS.latitude, selection.lat);
						this.setField(FIELD_KEYS.longitude, selection.lng);
					}

					this.setRelatedFields(selection);
				});
		});
	}
}

const deps = [modal];

export default angular
	.module("vesl.components.field-helpers.location-selector", deps)
	.component("locationSelector", {
		template: `
		<button type="button"
			class="btn btn-default"
			aria-label="Location Selector"
			uib-tooltip="Location Selector"
		tooltip-is-open="$ctrl.showTooltip"
        ng-disabled="$ctrl.lookup.disabled"
        ng-click="$ctrl.activateModal()">
          <i class="zmdi zmdi-gps-dot ma-icon"></i>
	  </button>
    `,
		controller: Controller,
		bindings: {
			lookup: "<",
			onSelect: "&"
		},
		require: {
			reportScope: "^^reportScope"
		}
	}).name;
